import { List, NeoModel } from "@singularsystems/neo-core";
import type {ITaskRunner } from "@singularsystems/neo-core";
import { Views } from "@singularsystems/neo-react";
import { action } from "mobx";
import { NotificationDuration } from "../../../App/Models/NotificationDuration";
import { AppService } from "../../../App/Services/AppService";
import { DomainTypes, Types } from "../../DomainTypes";
import DateCycle from "../../Models/Dividends/DateCycle";
import DateCycleLookup from "../../Models/Dividends/Lookups/DateCycleLookup";

@NeoModel
export default class DividendCyclesVM extends Views.ViewModelBase {

    constructor(
        taskRunner?: ITaskRunner,
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        private investmentSetupApiClient = AppService.get(DomainTypes.ApiClients.InvestmentSetup),
        private dataCache = AppService.get(DomainTypes.Services.DataCache)) {

        super(taskRunner);

        this.fetchDateCycles = this.fetchDateCycles.bind(this);
    }

    public dateCycleList = new List(DateCycleLookup);

    public get isDirty() {
        return this.editingDateCycle?.isDirty ?? false;
    }

    public async fetchDateCycles() {
        const data = await this.taskRunner.waitForData(this.investmentSetupApiClient.getDateCycles());
        this.dateCycleList.set(data);
    }

    public editingDateCycle: DateCycle | null = null;
    public isReadOnly = false;
    public isInvestmentSpecific = false;

    public async beginEditDateCycle(dateCycleId: number, isReadonly: boolean, investmentId: number | null = null) {

        const dateCycle = await this.waitForModel(DateCycle, this.investmentSetupApiClient.getDateCycle(dateCycleId, investmentId));
        this.editDateCycle(dateCycle, isReadonly || dateCycle.isLinkedToInvestments === true);
    }

    /**
     * Edit the provided dividend cycle. Fetches the cycle date list if the dividend cycle is not new.
     */
    public async editDateCycle(dateCycle: DateCycle, isReadonly: boolean) {

        this.isReadOnly = isReadonly;
        this.isInvestmentSpecific = dateCycle.forSingleInvestment;

        this.editingDateCycle = dateCycle;
    }

    public newDateCycle() {
        this.isReadOnly = false;
        this.editingDateCycle = new DateCycle();
    }

    public addMonths() {
        if (this.editingDateCycle) {
            for (let i = 1; i <= 12; i++) {
                const cycle = this.editingDateCycle.dates.addNew();
                cycle.month = i;
            }
        }
    }

    public async saveDateCycle(investmentId: number | null = null) {
        
        const DateCycle = this.editingDateCycle;

        await this.taskRunner.run(async () => {
            if (this.editingDateCycle) {

                const result = await this.investmentSetupApiClient.saveDateCycle(this.editingDateCycle.toJSObject(), investmentId);
                this.editingDateCycle.update(result.data);

                this.notifications.addSuccess("Dividend cycles", "Saved successfully.", NotificationDuration.Standard);

                let lookup = this.dateCycleList.find(c => c.dateCycleId === this.editingDateCycle!.dateCycleId) ?? this.dateCycleList.addNew();

                lookup.mapFrom({
                    dateCycleId: this.editingDateCycle.dateCycleId,
                    name: this.editingDateCycle.dateCycleName, 
                    description: this.editingDateCycle.description,
                    ordinal: this.editingDateCycle.ordinal,
                });

                this.dataCache.dateCycles.expire();
                
                this.editingDateCycle = null;
            }
        });

        return DateCycle!;
    }

    public async deleteDateCycle(dateCycle: DateCycleLookup) {
        await this.taskRunner.run(async () => {
                await this.investmentSetupApiClient.deleteDateCycle(dateCycle.dateCycleId);
                this.dateCycleList.removeWithoutTracking(dateCycle);
                this.dataCache.dateCycles.expire();
                this.notifications.addSuccess("Dividend cycles", "Deleted successfully.", NotificationDuration.Standard);
        });
    }
}